import React, { useEffect, useState } from "react";
import { PaymentRecipientDto, PaymentRecipientType, walletRepository } from "../../../../repositories/WalletRepository";
import { OPAAvatar } from "../../../../components/OPAAvatar";
import { BulkPayment, BulkTypes, getDisplayNameBulkPayment } from "../../../../models/BulkPayment";

interface LatestRecipientsProps {
    onSelectLnurl: (lnurl: string) => void;
    onSelectBulkPayment: (bulkPayment: BulkPayment) => void;
    className?: string;
}

export const LatestRecipients: React.FC<LatestRecipientsProps> = ({ onSelectLnurl, onSelectBulkPayment, className }) => {
    const [latestRecipients, setLatestRecipients] = useState<PaymentRecipientDto[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        walletRepository.getLatestRecipients()
            .then((response) => {
                setLatestRecipients(response.latest ?? []);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    if(loading)
        return (
            <div className={className}>
                <div className="text-lg font-semibold mb-1 mt-4 text-brand-orange">Recent recipients</div>
                <div className="h-[168px]"></div>
            </div>
        )

    if(latestRecipients.length === 0)
        return null;

    const getBulkRecipientLeading = (bulkPayment: BulkPayment) => {
        if (bulkPayment.type === BulkTypes.city) {
            return (
                <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center overflow-hidden">
                    {bulkPayment.imgUrl ? (
                        <img src={bulkPayment.imgUrl} className="w-full h-full object-cover" />
                    ) : (
                        <span className="material-symbols-outlined">location_city</span>
                    )}
                </div>
            );
        }

        if (bulkPayment.type === BulkTypes.distanceFromMyself) {
            return (
                <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center">
                    <span className="material-symbols-outlined">location_on</span>
                </div>
            );
        }

        if (bulkPayment.type === BulkTypes.userAttribute) {
            return (
                <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center">
                    <span className="material-symbols-outlined">label</span>
                </div>
            );
        }

        if (bulkPayment.imgUrl) {
            return (
                <OPAAvatar
                    imageUrl={bulkPayment.imgUrl}
                    size={40}
                    borderWidth={0}
                    borderColor="var(--brand-bg-2)"
                />
            );
        }

        return (
            <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center">
                <span className="material-symbols-outlined">group</span>
            </div>
        );
    };

    return (
        <div className={className}>
            <div className="text-lg font-semibold mb-1 mt-4 text-brand-orange">Recent addresses</div>
            <div className="max-h-[160px] overflow-y-auto overflow-x-hidden relative mx-[-10px]">
                {latestRecipients.map((recipient, index) => {
                    let className = index === latestRecipients.length - 1 ? "" : "border-b border-brand-border-color";
                    const singleRecipient = recipient.singleRecipient;

                    if(recipient.type === PaymentRecipientType.singleRecipient && singleRecipient) {
                        return (
                            <div key={index} className={`${className}`}>
                                <div
                                    onClick={() => onSelectLnurl(singleRecipient.lnurl)}
                                    className="flex items-center gap-2 py-2 px-[10px] cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    {singleRecipient.imageUrl && (
                                        <OPAAvatar
                                            imageUrl={singleRecipient.imageUrl}
                                            size={40}
                                            borderWidth={0}
                                            borderColor="var(--brand-bg-2)"
                                        />
                                    )}
                                    {singleRecipient.name}
                                </div>
                            </div>
                        );
                    }

                    const bulkRecipient = recipient.bulkRecipient;
                    if (recipient.type === PaymentRecipientType.bulkRecipient && bulkRecipient) {
                        const bulkPayment: BulkPayment = {
                            id: bulkRecipient.id,
                            type: bulkRecipient.type as BulkTypes,
                            name: bulkRecipient.name,
                            imgUrl: bulkRecipient.imageUrl,
                            recipientsCount: bulkRecipient.recipientsCount ?? 0
                        };

                        return (
                            <div key={index} className={`${className}`}>
                                <div
                                    onClick={() => onSelectBulkPayment(bulkPayment)}
                                    className="flex items-center gap-2 py-2 px-[10px] cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    {getBulkRecipientLeading(bulkPayment)}
                                    {getDisplayNameBulkPayment(bulkPayment)}
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );
}